import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Avatar,
  Divider,
  Snackbar,
  Alert,
  Tab,
  Tabs,
  Paper,
  Chip,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Save as SaveIcon,
  PhotoCamera as CameraIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Work as WorkIcon,
  LocationOn as LocationIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { RootState } from '../../store';

const ProfilePage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [showPassword, setShowPassword] = useState({ current: false, new: false, confirm: false });

  const [profile, setProfile] = useState({
    name: user?.fullName || '',
    email: user?.email || '',
    phone: user?.mobile || '',
    designation: '',
    department: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
  });

  const [password, setPassword] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProfile({ ...profile, [e.target.name]: e.target.value });
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword({ ...password, [e.target.name]: e.target.value });
  };

  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSnackbar({ open: true, message: 'Profile updated successfully!', severity: 'success' });
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password.newPassword !== password.confirmPassword) {
      setSnackbar({ open: true, message: 'Passwords do not match!', severity: 'error' });
      return;
    }
    setSnackbar({ open: true, message: 'Password changed successfully!', severity: 'success' });
    setPassword({ currentPassword: '', newPassword: '', confirmPassword: '' });
  };

  const getRoleColor = () => {
    const colors: Record<string, string> = {
      CIVILHEALTH_COORDINATOR: '#1a365d',
      SYSTEM_ADMIN: '#1a365d',
      GOVERNMENT_DOCTOR: '#059669',
      IAS_OFFICER: '#2563eb',
      IPS_OFFICER: '#dc2626',
      IFS_OFFICER: '#059669',
      IRS_OFFICER: '#7c3aed',
      JUDGE: '#1a365d',
      MINISTER: '#b91c1c',
    };
    return colors[user?.role || ''] || '#2563eb';
  };

  const getRoleDisplayName = () => {
    const roles: Record<string, string> = {
      CIVILHEALTH_COORDINATOR: 'Coordinator',
      SYSTEM_ADMIN: 'Admin',
      GOVERNMENT_DOCTOR: 'Doctor',
      IAS_OFFICER: 'IAS Officer',
      IPS_OFFICER: 'IPS Officer',
      IFS_OFFICER: 'IFS Officer',
      IRS_OFFICER: 'IRS Officer',
      JUDGE: 'Judge',
      MINISTER: 'Minister',
      OSD: 'OSD',
      SENIOR_GOVERNMENT_OFFICER: 'Senior Officer',
    };
    return roles[user?.role || ''] || 'User';
  };

  return (
    <>
      <Box sx={{ p: 2, flex: 1, overflow: 'auto', bgcolor: 'transparent' }}>
        {/* Header */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827' }}>My Profile</Typography>
            <Typography variant="body2" color="#6b7280">Manage your account settings and preferences</Typography>
          </Box>
        </Box>

        <Grid container spacing={2}>
          {/* Profile Card */}
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
              {/* Header Background */}
              <Box sx={{ height: 80, bgcolor: getRoleColor(), position: 'relative' }} />

              {/* Avatar & Info */}
              <Box sx={{ px: 2, pb: 2, mt: -4, textAlign: 'center' }}>
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      bgcolor: getRoleColor(),
                      fontSize: '2rem',
                      fontWeight: 700,
                      border: '4px solid #fff',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    }}
                  >
                    {user?.fullName?.charAt(0) || 'U'}
                  </Avatar>
                  <IconButton
                    size="small"
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      bgcolor: '#fff',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                      '&:hover': { bgcolor: 'transparent' },
                    }}
                  >
                    <CameraIcon fontSize="small" sx={{ color: '#6b7280' }} />
                  </IconButton>
                </Box>

                <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827', mt: 1.5 }}>
                  {user?.fullName || 'User'}
                </Typography>
                <Chip
                  label={getRoleDisplayName()}
                  size="small"
                  sx={{ mt: 0.5, bgcolor: `${getRoleColor()}15`, color: getRoleColor(), fontWeight: 600 }}
                />

                <Box sx={{ mt: 2, textAlign: 'left' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <EmailIcon sx={{ fontSize: 16, color: '#9ca3af' }} />
                    <Typography variant="body2" color="#6b7280" sx={{ fontSize: '0.8rem' }}>
                      {user?.email || 'Not provided'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <PhoneIcon sx={{ fontSize: 16, color: '#9ca3af' }} />
                    <Typography variant="body2" color="#6b7280" sx={{ fontSize: '0.8rem' }}>
                      {user?.mobile || 'Not provided'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WorkIcon sx={{ fontSize: 16, color: '#9ca3af' }} />
                    <Typography variant="body2" color="#6b7280" sx={{ fontSize: '0.8rem' }}>
                      {'Government of India'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Tabs & Content */}
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid #e5e7eb' }}>
              {/* Tabs */}
              <Box sx={{ borderBottom: '1px solid #e5e7eb' }}>
                <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} sx={{ px: 2 }}>
                  <Tab label="Profile Information" sx={{ textTransform: 'none', fontWeight: 600 }} />
                  <Tab label="Change Password" sx={{ textTransform: 'none', fontWeight: 600 }} />
                </Tabs>
              </Box>

              {/* Tab Content */}
              <Box sx={{ p: 2 }}>
                {activeTab === 0 && (
                  <form onSubmit={handleProfileSubmit}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Full Name"
                          name="name"
                          value={profile.name}
                          onChange={handleChange}
                          required
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><PersonIcon sx={{ fontSize: 18, color: '#9ca3af' }} /></InputAdornment>,
                          }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Email"
                          name="email"
                          type="email"
                          value={profile.email}
                          onChange={handleChange}
                          required
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><EmailIcon sx={{ fontSize: 18, color: '#9ca3af' }} /></InputAdornment>,
                          }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Phone"
                          name="phone"
                          value={profile.phone}
                          onChange={handleChange}
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ fontSize: 18, color: '#9ca3af' }} /></InputAdornment>,
                          }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Designation"
                          name="designation"
                          value={profile.designation}
                          disabled
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><WorkIcon sx={{ fontSize: 18, color: '#9ca3af' }} /></InputAdornment>,
                          }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Address"
                          name="address"
                          multiline
                          rows={2}
                          value={profile.address}
                          onChange={handleChange}
                          InputProps={{
                            startAdornment: <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}><LocationIcon sx={{ fontSize: 18, color: '#9ca3af' }} /></InputAdornment>,
                          }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="City"
                          name="city"
                          value={profile.city}
                          onChange={handleChange}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="State"
                          name="state"
                          value={profile.state}
                          onChange={handleChange}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Pincode"
                          name="pincode"
                          value={profile.pincode}
                          onChange={handleChange}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Button
                            type="submit"
                            variant="contained"
                            startIcon={<SaveIcon />}
                            sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600 }}
                          >
                            Save Changes
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </form>
                )}

                {activeTab === 1 && (
                  <form onSubmit={handlePasswordSubmit}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Current Password"
                          name="currentPassword"
                          type={showPassword.current ? 'text' : 'password'}
                          value={password.currentPassword}
                          onChange={handlePasswordChange}
                          required
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><LockIcon sx={{ fontSize: 18, color: '#9ca3af' }} /></InputAdornment>,
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setShowPassword({ ...showPassword, current: !showPassword.current })}>
                                  {showPassword.current ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="New Password"
                          name="newPassword"
                          type={showPassword.new ? 'text' : 'password'}
                          value={password.newPassword}
                          onChange={handlePasswordChange}
                          required
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setShowPassword({ ...showPassword, new: !showPassword.new })}>
                                  {showPassword.new ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Confirm New Password"
                          name="confirmPassword"
                          type={showPassword.confirm ? 'text' : 'password'}
                          value={password.confirmPassword}
                          onChange={handlePasswordChange}
                          required
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setShowPassword({ ...showPassword, confirm: !showPassword.confirm })}>
                                  {showPassword.confirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Button
                            type="submit"
                            variant="contained"
                            startIcon={<LockIcon />}
                            sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600 }}
                          >
                            Change Password
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </form>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} sx={{ borderRadius: 1.5 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ProfilePage;